import "./WorkflowTemplateStep.css";

import {
  ArrowDownDarkIcon,
  AsteriskButtonIcon,
  HamburgerMenuGreyIcon,
  RoundedMinusSignIcon,
  RoundedPlusSignIcon,
} from "../../../../../../../../../assets";
import { ColorConstants } from "../../../../../../../../../constants";
import { WorkflowTemplateTaskPanel } from "../../../../../../../../../templates";
import { Button, Column, CursorDataGrid, ProfileMenu, ProfileMenuItem, Row } from "../../../../../../../../../widget";
import { WorkflowTemplateStepModel } from "../../models";
import { DeleteTemplateStepModal, EditTemplateStepModal } from "./components";
import { useWorkflowTemplateStep } from "./useWorkflowTemplateStep";

interface WorkflowTemplateStepProps {
  workflowTemplateStep: WorkflowTemplateStepModel;
}

export const WorkflowTemplateStep = ({ workflowTemplateStep }: WorkflowTemplateStepProps): JSX.Element => {
  const {
    showStep,
    handleShowStep,
    stepActionsMenuOptionsList,
    workflowTemplateUuid,
    onChange,
    columns,
    isLoadingTasks,
    defaultSortingCriteria,

    showTaskPanel,
    setShowTaskPanel,

    showStepActionsMenu,
    setShowStepActionsMenu,

    showDeleteTemplateStepModal,
    setShowDeleteTemplateStepModal,

    showEditTemplateStepModal,
    setShowEditTemplateStepModal,

    templateTask,
    handleCloseTemplateTaskPanel,

    refreshWorkflowTemplate,
    onStepDrop,
    isContributorAndCanAddTasks,
    canEditStep,
  } = useWorkflowTemplateStep({
    workflowTemplateStep,
  });

  return (
    <div
      className="WorkflowTemplateStep"
      draggable={isContributorAndCanAddTasks && !showStep}
      onDrop={
        isContributorAndCanAddTasks
          ? (e) => onStepDrop(workflowTemplateStep.defaultSequence, e.dataTransfer.getData("stepUuid"))
          : undefined
      }
      onDragOver={isContributorAndCanAddTasks ? (ev) => ev.preventDefault() : undefined}
      onDragStart={
        isContributorAndCanAddTasks ? (ev) => ev.dataTransfer.setData("stepUuid", workflowTemplateStep.uuid) : undefined
      }
    >
      <Row spacingV={showStep ? "ml" : undefined}>
        <Column span={12} hasNoPadding>
          <div className="WorkflowTemplateStepContent">
            <div className="WorkflowTemplateStepLeft">
              {!showStep && isContributorAndCanAddTasks && <HamburgerMenuGreyIcon className="HamburgerMenuIcon" />}
              <div className="WorkflowTemplateStepText">
                <h2>{workflowTemplateStep.defaultStepName}</h2>
                <div className="WorkflowTemplateStepTaskCount">
                  {workflowTemplateStep.taskCount && workflowTemplateStep.taskCount > 0 ? (
                    <p className="body2Light">
                      {workflowTemplateStep.taskCount} task{workflowTemplateStep.taskCount > 1 ? "s" : ""}
                    </p>
                  ) : (
                    <p className="body2Light NoTasks">There are currently no tasks added to this step</p>
                  )}
                  {workflowTemplateStep.mandatoryTaskCount && workflowTemplateStep.mandatoryTaskCount > 0 ? (
                    <>
                      <p className="body2Light">|</p>
                      <AsteriskButtonIcon className="AsteriskIcon" />
                      <p className="body2">
                        {workflowTemplateStep.mandatoryTaskCount} mandatory task
                        {workflowTemplateStep.mandatoryTaskCount > 1 ? "s" : ""}
                      </p>
                    </>
                  ) : undefined}
                </div>
              </div>
            </div>
            {((!isContributorAndCanAddTasks && workflowTemplateStep.taskCount && workflowTemplateStep.taskCount > 0) ||
              isContributorAndCanAddTasks) && (
              <Button
                className="SeeTasksButton"
                // eslint-disable-next-line no-nested-ternary
                text={showStep ? "Hide details" : "Show details"}
                variant="quaternary"
                hasNoPadding
                size="small"
                icon={
                  showStep ? (
                    <RoundedMinusSignIcon className="RoundedMinusSignIcon" />
                  ) : (
                    <RoundedPlusSignIcon className="RoundedPlusSignIcon" />
                  )
                }
                onClick={() => handleShowStep(!showStep)}
              />
            )}
          </div>
        </Column>
      </Row>
      {showStep && (
        <div className="ExpandedWorkflowTemplateStepContent">
          <Row>
            <Column span={12} hasNoPadding>
              <CursorDataGrid
                data={[]}
                onChange={onChange}
                dataIsLoading={isLoadingTasks}
                columns={columns}
                clientSide={false}
                hasNoPadding
                hasBottomMargin={false}
                showTotalCount={false}
                refresh={refreshWorkflowTemplate}
                defaultSortingCriteria={defaultSortingCriteria}
              />
            </Column>
          </Row>
          {isContributorAndCanAddTasks && (
            <Row>
              <Column span={12} hasNoPadding>
                <div className="WorkflowTemplateStepButtons">
                  {canEditStep && (
                    <div className="StepActions">
                      <Button
                        text="Actions"
                        variant="reversedPrimary"
                        hasNoPadding
                        size="small"
                        icon={<ArrowDownDarkIcon stroke={ColorConstants.INDIGO_500} />}
                        onClick={() => setShowStepActionsMenu(true)}
                      />
                      <ProfileMenu
                        show={showStepActionsMenu}
                        onClose={() => setShowStepActionsMenu(false)}
                        position={{ top: "0" }}
                      >
                        {stepActionsMenuOptionsList.map((el) => (
                          <ProfileMenuItem
                            key={el.id}
                            profileMenuItem={el}
                            onClick={() => {
                              if (el.action) {
                                el.action();
                              }
                              setShowStepActionsMenu(false);
                            }}
                          />
                        ))}
                      </ProfileMenu>
                    </div>
                  )}
                  <Button text="Add task" hasNoPadding size="small" onClick={() => setShowTaskPanel(true)} />
                </div>
              </Column>
            </Row>
          )}
        </div>
      )}
      <DeleteTemplateStepModal
        show={showDeleteTemplateStepModal}
        closeModal={() => setShowDeleteTemplateStepModal(false)}
        templateUuid={workflowTemplateUuid}
        templateStepUuid={workflowTemplateStep.uuid}
        templateStepRowVersion={workflowTemplateStep.rowVersion}
      />
      <EditTemplateStepModal
        show={showEditTemplateStepModal}
        closeModal={() => setShowEditTemplateStepModal(false)}
        workflowTemplateStep={workflowTemplateStep}
      />
      {showTaskPanel && (
        <WorkflowTemplateTaskPanel
          templateTask={templateTask}
          currentStepUuid={showTaskPanel ? workflowTemplateStep.uuid : undefined}
          show={showTaskPanel}
          onClose={handleCloseTemplateTaskPanel}
        />
      )}
    </div>
  );
};
